import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  apiClient,
  type DocumentDeliveryMethodDto,
  type QuestionnaireTemplateDto,
} from '@/api/client';
import { queryKeys } from '@/query/keys';

import type { QuestionnaireConfigDto } from './endpoints';

/** Normalised view model — wire DTO has optional fields, the UI wants non-null. */
export interface AdminQuestionnaireTemplateViewModel {
  readonly id: string;
  readonly templateKey: string;
  readonly label: string;
  readonly active: boolean;
  readonly emailSubject: string;
  readonly emailBodyHtml: string;
  readonly emailBodyText: string;
  /** EmailServer guid to send from; '' = tenant default. */
  readonly emailServerId: string;
}

export function mapAdminQuestionnaireTemplate(
  dto: QuestionnaireTemplateDto,
): AdminQuestionnaireTemplateViewModel {
  return {
    id: dto.id ?? '',
    templateKey: dto.template_key ?? '',
    label: dto.label ?? '',
    // BE column is NOT NULL default true; `?? true` keeps the FE safe if the contract drifts.
    active: dto.active ?? true,
    emailSubject: dto.email_subject ?? '',
    emailBodyHtml: dto.email_body_html ?? '',
    emailBodyText: dto.email_body_text ?? '',
    emailServerId: dto.email_server_id ?? '',
  };
}

export interface AdminQuestionnaireTemplateInput {
  readonly templateKey: string;
  readonly label: string;
  readonly active: boolean;
  readonly emailSubject: string;
  readonly emailBodyHtml: string;
  readonly emailBodyText: string;
  readonly emailServerId: string;
}

export interface UpdateAdminQuestionnaireTemplateInput extends AdminQuestionnaireTemplateInput {
  readonly id: string;
}

function toDto(input: AdminQuestionnaireTemplateInput, id?: string): QuestionnaireTemplateDto {
  const serverId = input.emailServerId.trim();
  return {
    ...(id !== undefined ? { id } : {}),
    template_key: input.templateKey,
    label: input.label,
    active: input.active,
    email_subject: input.emailSubject,
    email_body_html: input.emailBodyHtml,
    email_body_text: input.emailBodyText,
    // Send only when a specific server is chosen; the BE treats null as "no change / default".
    ...(serverId.length > 0 ? { email_server_id: serverId } : {}),
  };
}

export const adminQuestionnaireTemplatesQueryKey = queryKeys.questionnaireTemplates;

export function useAdminQuestionnaireTemplates(): UseQueryResult<
  AdminQuestionnaireTemplateViewModel[]
> {
  return useQuery<AdminQuestionnaireTemplateViewModel[]>({
    queryKey: adminQuestionnaireTemplatesQueryKey,
    queryFn: async ({ signal }) => {
      const page = await apiClient.getQuestionnaireTemplates({ size: 200, signal });
      return page.content.map(mapAdminQuestionnaireTemplate);
    },
  });
}

/** Delivery channels (stubbed). Shared with the patient-record Send dialog. */
export function useQuestionnaireDeliveryMethods(): UseQueryResult<DocumentDeliveryMethodDto[]> {
  return useQuery<DocumentDeliveryMethodDto[]>({
    queryKey: queryKeys.questionnaireDeliveryMethods,
    queryFn: ({ signal }) => apiClient.getQuestionnaireDeliveryMethods({ signal }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Whether sending questionnaires is configured for the current tenant. Gates the patient-record
 * "Send questionnaire" action and surfaces the admin "configure the URL" hint.
 */
export function useQuestionnaireConfig(): UseQueryResult<QuestionnaireConfigDto> {
  return useQuery<QuestionnaireConfigDto>({
    queryKey: queryKeys.questionnaireConfig,
    queryFn: ({ signal }) => apiClient.getQuestionnaireConfig({ signal }),
    staleTime: 5 * 60 * 1000,
  });
}

type Snapshot = AdminQuestionnaireTemplateViewModel[] | undefined;

export function useAdminQuestionnaireTemplateMutations(): {
  readonly create: UseMutationResult<
    AdminQuestionnaireTemplateViewModel,
    Error,
    AdminQuestionnaireTemplateInput
  >;
  readonly update: UseMutationResult<
    AdminQuestionnaireTemplateViewModel,
    Error,
    UpdateAdminQuestionnaireTemplateInput,
    { previous: Snapshot }
  >;
  readonly remove: UseMutationResult<undefined, Error, string, { previous: Snapshot }>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: adminQuestionnaireTemplatesQueryKey })
      .catch(() => undefined);
  };

  const create = useMutation<
    AdminQuestionnaireTemplateViewModel,
    Error,
    AdminQuestionnaireTemplateInput
  >({
    mutationFn: async (input) =>
      mapAdminQuestionnaireTemplate(await apiClient.createQuestionnaireTemplate(toDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<
    AdminQuestionnaireTemplateViewModel,
    Error,
    UpdateAdminQuestionnaireTemplateInput,
    { previous: Snapshot }
  >({
    mutationFn: async ({ id, ...rest }) =>
      mapAdminQuestionnaireTemplate(await apiClient.updateQuestionnaireTemplate(id, toDto(rest, id))),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: adminQuestionnaireTemplatesQueryKey });
      const previous = queryClient.getQueryData<AdminQuestionnaireTemplateViewModel[]>(
        adminQuestionnaireTemplatesQueryKey,
      );
      if (previous !== undefined) {
        queryClient.setQueryData<AdminQuestionnaireTemplateViewModel[]>(
          adminQuestionnaireTemplatesQueryKey,
          previous.map((row) =>
            row.id === input.id
              ? {
                  id: input.id,
                  templateKey: input.templateKey,
                  label: input.label,
                  active: input.active,
                  emailSubject: input.emailSubject,
                  emailBodyHtml: input.emailBodyHtml,
                  emailBodyText: input.emailBodyText,
                  emailServerId: input.emailServerId,
                }
              : row,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(adminQuestionnaireTemplatesQueryKey, context.previous);
      }
    },
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string, { previous: Snapshot }>({
    mutationFn: (id) => apiClient.deleteQuestionnaireTemplate(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: adminQuestionnaireTemplatesQueryKey });
      const previous = queryClient.getQueryData<AdminQuestionnaireTemplateViewModel[]>(
        adminQuestionnaireTemplatesQueryKey,
      );
      if (previous !== undefined) {
        queryClient.setQueryData<AdminQuestionnaireTemplateViewModel[]>(
          adminQuestionnaireTemplatesQueryKey,
          previous.filter((row) => row.id !== id),
        );
      }
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(adminQuestionnaireTemplatesQueryKey, context.previous);
      }
    },
    onSettled: invalidate,
  });

  return { create, update, remove };
}
